import { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import { UserContext } from '../context/UserContext';
import { resolveMediaUrl } from '../utils/url';
import './home.css';

export default function MentorMyTrainees() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [data, setData] = useState({ mentor: null, assignments: [], history: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [messageTarget, setMessageTarget] = useState(null); // {id, displayName, username}
  const [messageBody, setMessageBody] = useState('');
  const [messageSending, setMessageSending] = useState(false);
  const [messageError, setMessageError] = useState('');
  const [messageSuccess, setMessageSuccess] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [checklistAssignmentId, setChecklistAssignmentId] = useState(null);
  const [checklistItems, setChecklistItems] = useState([]); // {key, group, checked, checkedAt, note}
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [checklistSaving, setChecklistSaving] = useState(false);
  const [checklistError, setChecklistError] = useState('');

  const checklistGroups = useMemo(() => {
    const map = {};
    for (const it of checklistItems) {
      if (!map[it.group]) map[it.group] = [];
      map[it.group].push(it);
    }
    return map;
  }, [checklistItems]);

  async function openChecklist(assignmentId) {
    if (!assignmentId) return;
    setChecklistAssignmentId(assignmentId);
    setChecklistOpen(true);
    setChecklistError('');
    setChecklistItems([]);
    setChecklistLoading(true);
    try {
      const res = await api.get(`/admin/mentor-mappings/${assignmentId}/checklist`);
      setChecklistItems(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (e) {
      setChecklistError(e?.response?.data?.error || 'Checklist konnte nicht geladen werden');
    } finally {
      setChecklistLoading(false);
    }
  }

  function toggleChecklistItem(key) {
    setChecklistItems(items => items.map(it => it.key === key ? {
      ...it,
      checked: !it.checked,
      checkedAt: !it.checked ? new Date().toISOString() : null
    } : it));
  }

  function updateChecklistNote(key, note) {
    setChecklistItems(items => items.map(it => it.key === key ? { ...it, note } : it));
  }

  async function saveChecklist() {
    if (!checklistAssignmentId) return;
    setChecklistSaving(true);
    setChecklistError('');
    try {
      await api.post(`/admin/mentor-mappings/${checklistAssignmentId}/checklist`, {
        items: checklistItems.map(it => ({ key: it.key, checked: it.checked, note: it.note }))
      });
      // Reload to get authoritative timestamps
      const res = await api.get(`/admin/mentor-mappings/${checklistAssignmentId}/checklist`);
      setChecklistItems(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (e) {
      setChecklistError(e?.response?.data?.error || 'Speichern fehlgeschlagen');
    } finally {
      setChecklistSaving(false);
    }
  }

  function openMessageModal(userObj) {
    if (!userObj || !userObj.id) return;
    setMessageTarget({ id: userObj.id, displayName: userObj.displayName || userObj.username || `User #${userObj.id}`, username: userObj.username || null });
    setMessageBody('');
    setMessageError('');
    setMessageSuccess(false);
    setMessageModalOpen(true);
  }

  async function sendPrivateMessage(e) {
    if (e) e.preventDefault();
    if (!messageTarget || !messageTarget.id) return;
    const body = messageBody.trim();
    if (!body) {
      setMessageError('Bitte eine Nachricht eingeben.');
      return;
    }
    setMessageSending(true);
    setMessageError('');
    setMessageSuccess(false);
    try {
      await api.post('/messages/send', { recipientId: messageTarget.id, body });
      setMessageSuccess(true);
      // Optional: nach kurzer Zeit schließen
      setTimeout(() => { setMessageModalOpen(false); }, 1200);
    } catch (err) {
      setMessageError(err?.response?.data?.error || 'Nachricht konnte nicht gesendet werden.');
    } finally {
      setMessageSending(false);
    }
  }

  useEffect(() => {
    const load = async () => {
      if (!user) return;
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/admin/mentor-mappings/by-mentor/${user.id}`);
        setData(res.data || { mentor: null, assignments: [], history: [] });
      } catch (e) {
        setError(e?.response?.data?.error || 'Konnte Daten nicht laden');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  const isMentor = Array.isArray(user?.roles) && user.roles.includes('mentor');

  const activeFiltered = useMemo(() => {
    let items = data.assignments || [];
    if (filterStatus) {
      items = items.filter(a => {
        const st = (a.trainee?.status || '').toLowerCase();
        return st === filterStatus.toLowerCase();
      });
    }
    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      items = items.filter(a => {
        const t = a.trainee || {};
        const referred = (t.referrer ? (t.referrer.displayName || t.referrer.username) : '') || '';
        return (
          (t.displayName || '').toLowerCase().includes(q) ||
          (t.username || '').toLowerCase().includes(q) ||
          referred.toLowerCase().includes(q)
        );
      });
    }
    return items;
  }, [data.assignments, filterStatus, searchTerm]);

  const historyFiltered = useMemo(() => {
    let items = data.history || [];
    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      items = items.filter(a => {
        const t = a.trainee || {};
        const referred = (t.referrer ? (t.referrer.displayName || t.referrer.username) : '') || '';
        return (
          (t.displayName || '').toLowerCase().includes(q) ||
          (t.username || '').toLowerCase().includes(q) ||
          referred.toLowerCase().includes(q)
        );
      });
    }
    return items;
  }, [data.history, searchTerm]);

  if (!user) {
    return (
      <div className="home-page" style={{ padding: '40px 20px', textAlign: 'center' }}>
        <h2 style={{ marginTop: 0 }}>Nicht eingeloggt</h2>
        <p style={{ maxWidth: 640, margin: '12px auto', lineHeight: 1.5 }}>Bitte melde dich an, um deine Anwärter zu sehen.</p>
      </div>
    );
  }

  if (!isMentor) {
    return (
      <div className="home-page" style={{ padding: '40px 20px', textAlign: 'center' }}>
        <h2 style={{ marginTop: 0 }}>Keine Mentor-Rolle</h2>
        <p style={{ maxWidth: 640, margin: '12px auto', lineHeight: 1.5 }}>Diese Seite ist nur für Mentoren sichtbar.</p>
      </div>
    );
  }

  return (
    <>
    <div className="home-page">
      <section className={user ? 'hero-offset-if-sidebar' : ''} style={{ width:'100vw', margin:0, padding:0 }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin:'0 0 8px' }}>Meine Anwärter im Mentoren-Programm</h2>
        <p className="hero-desc" style={{ textAlign: 'center', margin:'0 auto 24px', maxWidth:760 }}>Überblick über alle Anwärter, die du aktuell oder in der Vergangenheit betreut hast.</p>
        <p style={{ textAlign: 'center', maxWidth: 820, margin: '0 auto', color: '#D4D8DF' }}>
          Nutze diese Seite als Ausgangspunkt für deine Mentorings: behalte den Überblick, sieh Entwicklungen im Rückblick und finde schnell die passenden Profile für Rückfragen oder Follow-Ups.
        </p>
        <p style={{ textAlign: 'center', maxWidth: 820, margin: '0 auto', color: '#D4D8DF' }}>
          Beachte immer folgendes: Vertraulichkeit und Respekt sind essenziell.
        </p>
        <p style={{ textAlign: 'center', maxWidth: 820, margin: '0 auto', color: '#D4D8DF' }}>
          Die Informationen zu deinen Anwärtern sind privat und sollten mit Sorgfalt behandelt werden. Nutze die Daten ausschließlich für das Mentoring und teile sie nicht ohne ausdrückliche Zustimmung der betreffenden Personen.
        </p>
        <div style={{ maxWidth: 820, margin: '10px auto 0', color: '#D4D8DF', padding: '0 16px' }}>
          <div style={{ fontWeight: 700, color: '#f0abfc', textAlign: 'center', marginBottom: 8 }}>Mini-Faustregeln für Mentoren (intern):</div>
          <ul style={{
            margin: 0,
            padding: 0,
            listStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            alignItems: 'center'
          }}>
            <li style={{ display:'flex', alignItems:'center', gap:10, justifyContent:'center' }}>
              <span aria-hidden="true" style={{ fontSize:18 }}>🤝</span>
              <span>Mentor sein heißt begleiten, nicht bewerten.</span>
              <span>🤝</span>
            </li>
            <li style={{ display:'flex', alignItems:'center', gap:10, justifyContent:'center' }}>
              <span aria-hidden="true" style={{ fontSize:18 }}>🎯</span>
              <span>Qualität vor Quantität: Lieber 1 Mentee gut als 2 halb.</span>
              <span>🎯</span>
            </li>
            <li style={{ display:'flex', alignItems:'center', gap:10, justifyContent:'center' }}>
              <span aria-hidden="true" style={{ fontSize:18 }}>👂</span>
              <span>Erst zuhören, dann helfen.</span>
              <span>👂</span>
            </li>
            <li style={{ display:'flex', alignItems:'center', gap:10, justifyContent:'center' }}>
              <span aria-hidden="true" style={{ fontSize:18 }}>🔍</span>
              <span>Keine Lösung ohne Kontext: immer erst verstehen, dann empfehlen.</span>
              <span>🔍</span>
            </li>
            <li style={{ display:'flex', alignItems:'center', gap:10, justifyContent:'center' }}>
              <span aria-hidden="true" style={{ fontSize:18 }}>🌟</span>
              <span>Immer positiv rahmen: Kritik = Chance, nicht Urteil.</span>
              <span>🌟</span>
            </li>
            <li style={{ display:'flex', alignItems:'center', gap:10, justifyContent:'center' }}>
              <span aria-hidden="true" style={{ fontSize:18 }}>📅</span>
              <span>Aktivität nachweisbar halten: mind. 1 Interaktion pro Woche.</span>
              <span>📅</span>
            </li>
          </ul>
        </div>
        <div style={{ height: 12 }} />
        <div style={{ display:'flex', justifyContent:'center', margin:'0 0 18px' }}>
          <button
            type="button"
            onClick={() => setGuideOpen(true)}
            style={{
              display:'inline-flex',
              alignItems:'center',
              gap:10,
              padding:'10px 16px',
              borderRadius:12,
              border:'1px solid rgba(56,189,248,0.5)',
              background:'linear-gradient(135deg, rgba(2,132,199,0.25), rgba(59,130,246,0.25))',
              color:'#e0f2fe',
              fontWeight:700,
              letterSpacing:.2,
              boxShadow:'0 6px 18px rgba(2,132,199,0.25)',
              cursor:'pointer'
            }}
          >
            <span style={{ fontSize:18 }}>📘</span>
            <span>Interner Leitfaden für Mentoren</span>
          </button>
        </div>
      </section>

      <section style={{ width: '100%', maxWidth: 1200, margin: '0 auto', padding: '0 16px 60px', color: '#fff' }}>
        {error && (
          <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, background: 'rgba(220,38,38,0.15)', border: '1px solid rgba(248,113,113,0.6)', color: '#fecaca' }}>
            {error}
          </div>
        )}
        {loading ? (
          <p>Lade Daten...</p>
        ) : (
          <>
          
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16, alignItems: 'center' }}>
            <div style={{ fontSize: 13, opacity: 0.8 }}>
              <strong>Aktive Anwärter:</strong> {data.assignments?.length || 0} · <strong>Historie:</strong> {data.history?.length || 0}
              {data.mentor && (
                <>
                  {' '}· <strong>Mentor seit:</strong>{' '}
                  {data.mentor.createdAt ? new Date(data.mentor.createdAt).toLocaleDateString() : '-'}
                </>
              )}
            </div>
            <div style={{ flexGrow: 1 }} />
            <input
              type="text"
              placeholder="Nach Name oder @username filtern"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ minWidth: 200, maxWidth: 260, flexShrink: 0, padding: '6px 10px', borderRadius: 999, border: '1px solid rgba(148,163,184,0.6)', background: '#020617', color: '#e5e7eb', fontSize: 13 }}
            />
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid rgba(148,163,184,0.6)', background: '#020617', color: '#e5e7eb', fontSize: 13 }}
            >
              <option value="">Status: alle</option>
              <option value="pending">pending</option>
              <option value="active">active</option>
              <option value="rejected">rejected</option>
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, alignItems: 'stretch' }}>
            <div className="highlight-card" style={{ padding: 20 }}>
              <h2 className="highlight-title">Aktuell zugeordnete Anwärter</h2>
              <p style={{ color: '#cbd5e1', lineHeight: 1.5, fontSize: 14 }}>
                Du kannst pro Mentor in der Regel bis zu zwei Anwärter gleichzeitig betreuen. Diese Liste zeigt deine derzeit aktiven Zuordnungen.
              </p>
              <div style={{ marginTop: 10, maxHeight: 480, overflow: 'auto' }}>
                {(!activeFiltered || activeFiltered.length === 0) ? (
                  <p style={{ color: '#9ca3af', fontSize: 14 }}>Dir sind aktuell keine Anwärter zugeordnet.</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
                        <th style={{ padding: '4px 6px' }}></th>
                        <th style={{ padding: '4px 6px' }}>Anwärter</th>
                        <th style={{ padding: '4px 6px' }}>Seit</th>
                        <th style={{ padding: '4px 6px' }}>Status</th>
                        <th style={{ padding: '4px 6px' }}>Lifetime‑Punkte</th>
                        <th style={{ padding: '4px 6px' }}>Weekly‑Punkte</th>
                        <th style={{ padding: '4px 6px' }}>Geworben durch</th>
                        <th style={{ padding: '4px 6px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeFiltered.map(a => {
                        const t = a.trainee || {};
                        return (
                          <tr key={a.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                            <td style={{ padding: '4px 6px', width: 42 }}>
                              <img
                                src={resolveMediaUrl(t.avatarUrl || '/avatars/default-avatar.png')}
                                alt="Avatar"
                                style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', background: '#020617' }}
                              />
                            </td>
                            <td style={{ padding: '4px 6px' }}>
                              <div style={{ fontWeight: 600 }}>
                                <Link to={`/u/${t.id}`} style={{ color: '#fff', textDecoration: 'none' }}>
                                  {t.displayName || t.username || `User #${t.id}`}
                                </Link>
                              </div>
                              {t.username && (
                                <div style={{ fontSize: 11, opacity: 0.7 }}>@{t.username}</div>
                              )}
                            </td>
                            <td style={{ padding: '4px 6px', fontSize: 12 }}>
                              {a.assignedAt ? new Date(a.assignedAt).toLocaleDateString() : '-'}
                            </td>
                            <td style={{ padding: '4px 6px', fontSize: 12 }}>
                              <span style={{ opacity: 0.9, fontSize: 11, borderRadius: 999, padding: '2px 8px', background: 'rgba(148,163,184,0.18)' }}>{t.status || '-'}</span>
                            </td>
                            <td style={{ padding: '4px 6px', fontSize: 12 }}>
                              {typeof t.lifetimePoints === 'number' ? t.lifetimePoints.toLocaleString('de-DE') : '—'}
                            </td>
                            <td style={{ padding: '4px 6px', fontSize: 12 }}>
                              {typeof t.weeklyPoints === 'number' ? t.weeklyPoints.toLocaleString('de-DE') : '0'}
                            </td>
                            <td style={{ padding: '4px 6px', fontSize: 12 }}>
                              {t.referrer ? (
                                <span style={{ opacity: 0.8 }}>
                                  {t.referrer.displayName || t.referrer.username || `User #${t.referrer.id}`}
                                </span>
                              ) : (
                                <span style={{ opacity: 0.4 }}>—</span>
                              )}
                            </td>
                            <td style={{ padding: '4px 6px', fontSize: 12, textAlign: 'right', whiteSpace:'nowrap', display:'flex', gap:6, justifyContent:'flex-end' }}>
                              <button
                                type="button"
                                onClick={() => t.id && openMessageModal(t)}
                                style={{
                                  fontSize: 11,
                                  padding: '4px 10px',
                                  borderRadius: 999,
                                  border: '1px solid rgba(96,165,250,0.6)',
                                  background: 'rgba(15,23,42,0.9)',
                                  color: '#bfdbfe',
                                  whiteSpace: 'nowrap',
                                  opacity: t.id ? 1 : 0.3,
                                  pointerEvents: t.id ? 'auto' : 'none',
                                  cursor: t.id ? 'pointer' : 'default'
                                }}
                              >
                                Nachricht
                              </button>
                              <button
                                type="button"
                                onClick={() => openChecklist(a.id)}
                                style={{
                                  fontSize: 11,
                                  padding: '4px 10px',
                                  borderRadius: 999,
                                  border: '1px solid rgba(16,185,129,0.6)',
                                  background: 'rgba(6,78,59,0.85)',
                                  color: '#6ee7b7',
                                  whiteSpace: 'nowrap',
                                  cursor: 'pointer'
                                }}
                              >
                                Checkliste öffnen
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="highlight-card" style={{ padding: 20 }}>
              <h2 className="highlight-title">Frühere Anwärter</h2>
              <p style={{ color: '#cbd5e1', lineHeight: 1.5, fontSize: 14 }}>
                Hier siehst du Anwärter, die du in der Vergangenheit betreut hast. Die Liste hilft dir, Entwicklungen nachzuvollziehen oder bei Bedarf erneut Kontakt aufzunehmen.
              </p>
              <div style={{ marginTop: 10, maxHeight: 480, overflow: 'auto' }}>
                {(!historyFiltered || historyFiltered.length === 0) ? (
                  <p style={{ color: '#9ca3af', fontSize: 14 }}>Bisher sind keine früheren Anwärter erfasst.</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
                        <th style={{ padding: '4px 6px' }}></th>
                        <th style={{ padding: '4px 6px' }}>Anwärter</th>
                        <th style={{ padding: '4px 6px' }}>Zeitraum</th>
                        <th style={{ padding: '4px 6px' }}>Aktueller Status</th>
                        <th style={{ padding: '4px 6px' }}>Geworben durch</th>
                        <th style={{ padding: '4px 6px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyFiltered.map(a => {
                        const t = a.trainee || {};
                        return (
                          <tr key={a.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                            <td style={{ padding: '4px 6px', width: 42 }}>
                              <img
                                src={resolveMediaUrl(t.avatarUrl || '/avatars/default-avatar.png')}
                                alt="Avatar"
                                style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', background: '#020617' }}
                              />
                            </td>
                            <td style={{ padding: '4px 6px' }}>
                              <div style={{ fontWeight: 600 }}>
                                <Link to={`/u/${t.id}`} style={{ color: '#fff', textDecoration: 'none' }}>
                                  {t.displayName || t.username || `User #${t.id}`}
                                </Link>
                              </div>
                              {t.username && (
                                <div style={{ fontSize: 11, opacity: 0.7 }}>@{t.username}</div>
                              )}
                            </td>
                            <td style={{ padding: '4px 6px', fontSize: 12 }}>
                              {a.assignedAt ? new Date(a.assignedAt).toLocaleDateString() : '-'}
                              {' – '}
                              {a.endedAt ? new Date(a.endedAt).toLocaleDateString() : '-'}
                            </td>
                            <td style={{ padding: '4px 6px', fontSize: 12 }}>
                              <span style={{ opacity: 0.9, fontSize: 11, borderRadius: 999, padding: '2px 8px', background: 'rgba(148,163,184,0.18)' }}>{t.status || '-'}</span>
                            </td>
                            <td style={{ padding: '4px 6px', fontSize: 12 }}>
                              {t.referrer ? (
                                <span style={{ opacity: 0.8 }}>
                                  {t.referrer.displayName || t.referrer.username || `User #${t.referrer.id}`}
                                </span>
                              ) : (
                                <span style={{ opacity: 0.4 }}>—</span>
                              )}
                            </td>
                            <td style={{ padding: '4px 6px', fontSize: 12, textAlign: 'right' }}>
                              <button
                                type="button"
                                onClick={() => t.id && openMessageModal(t)}
                                style={{
                                  fontSize: 11,
                                  padding: '4px 10px',
                                  borderRadius: 999,
                                  border: '1px solid rgba(148,163,184,0.5)',
                                  background: 'rgba(15,23,42,0.9)',
                                  color: '#e5e7eb',
                                  whiteSpace: 'nowrap',
                                  opacity: t.id ? 1 : 0.3,
                                  pointerEvents: t.id ? 'auto' : 'none',
                                  cursor: t.id ? 'pointer' : 'default'
                                }}
                              >
                                Kontakt aufnehmen
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
          </>
        )}
      </section>
    </div>
    {messageModalOpen && (
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }} onClick={()=> !messageSending && setMessageModalOpen(false)}>
        <div role="dialog" aria-modal="true" aria-label="Private Nachricht senden" onClick={e=> e.stopPropagation()} style={{ width:'100%', maxWidth:520, background:'#0f172a', border:'1px solid #334155', borderRadius:16, padding:24, boxShadow:'0 10px 30px -5px rgba(0,0,0,0.5)', color:'#fff' }}>
          <h2 style={{ marginTop:0, fontSize:20 }}>Nachricht an {messageTarget?.displayName || messageTarget?.username || 'Benutzer'}</h2>
          <p style={{ fontSize:13, lineHeight:1.4, color:'#cbd5e1', marginTop:4 }}>Diese Nachricht wird als private Nachricht gesendet.</p>
          <form onSubmit={sendPrivateMessage} style={{ marginTop:16, display:'flex', flexDirection:'column', gap:12 }}>
            <textarea
              autoFocus
              rows={6}
              value={messageBody}
              onChange={e=> setMessageBody(e.target.value)}
              placeholder="Deine Nachricht..."
              disabled={messageSending}
              style={{ width:'100%', resize:'vertical', padding:'12px 14px', borderRadius:12, background:'#1e293b', border:'1px solid #334155', color:'#e2e8f0', fontSize:14, lineHeight:1.5 }}
            />
            {messageError && <div style={{ background:'rgba(220,38,38,0.15)', border:'1px solid rgba(248,113,113,0.4)', color:'#fecaca', padding:'8px 10px', borderRadius:8, fontSize:12 }}>{messageError}</div>}
            {messageSuccess && <div style={{ background:'rgba(34,197,94,0.15)', border:'1px solid rgba(34,197,94,0.5)', color:'#86efac', padding:'8px 10px', borderRadius:8, fontSize:12 }}>Nachricht gesendet ✓</div>}
            <div style={{ display:'flex', gap:12, justifyContent:'flex-end', marginTop:4 }}>
              <button type="button" onClick={()=> !messageSending && setMessageModalOpen(false)} style={{ background:'#1e293b', color:'#e2e8f0', border:'1px solid #334155', borderRadius:999, padding:'10px 18px', fontSize:13, cursor: messageSending ? 'default' : 'pointer', opacity: messageSending ? .5 : 1 }}>Abbrechen</button>
              <button type="submit" disabled={messageSending || !messageBody.trim()} style={{ background: messageSending || !messageBody.trim() ? '#3b4252' : '#3b82f6', color:'#fff', border:'1px solid #2563eb', borderRadius:999, padding:'10px 20px', fontSize:13, cursor: (messageSending || !messageBody.trim()) ? 'not-allowed' : 'pointer', opacity: messageSending ? .7 : 1 }}>
                {messageSending ? 'Senden…' : 'Senden'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}
    {guideOpen && (
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }} onClick={()=> setGuideOpen(false)}>
        <div role="dialog" aria-modal="true" aria-label="Interner Leitfaden für Mentoren" onClick={e=> e.stopPropagation()} style={{ width:'100%', maxWidth:880, maxHeight:'85vh', overflow:'hidden', background:'#0b1220', border:'1px solid #334155', borderRadius:16, boxShadow:'0 14px 40px rgba(0,0,0,0.55)', color:'#e2e8f0' }}>
          <div style={{ padding:'18px 22px', borderBottom:'1px solid #243041', display:'flex', alignItems:'center', justifyContent:'space-between', background:'linear-gradient(180deg, rgba(30,58,138,0.35), rgba(2,6,23,0))' }}>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <span style={{ fontSize:22 }}>📘</span>
              <h2 style={{ margin:0, fontSize:20 }}>Interner Leitfaden für Mentoren (Woche 1–4)</h2>
            </div>
            <button type="button" onClick={()=> setGuideOpen(false)} aria-label="Schließen" style={{ background:'transparent', border:'1px solid #334155', color:'#93c5fd', borderRadius:10, padding:'6px 10px', cursor:'pointer' }}>Schließen</button>
          </div>
          <div style={{ padding:22, overflowY:'auto', maxHeight:'calc(85vh - 70px)' }}>
            <div style={{ marginBottom:14, color:'#cbd5e1' }}>
              <div style={{ fontSize:15, marginBottom:8 }}><strong>Ziel:</strong> Ein klarer Ablauf, wie Mentoring idealerweise läuft.</div>
              <div style={{ fontSize:14, lineHeight:1.5 }}>
                <strong>Hinweis:</strong> Dieser Leitfaden soll bei der Orientierung helfen, sicherstellen das nichts vergessen wird und für Konsistenz im Network sorgen. Es ist keine Schritt-für-Schritt Anleitung die exakt so befolgt werden muss, weder inhaltlich, zeitlich noch von der Reihenfolge. Passe es an dich und die Bedürfnisse deines Anwärters (Mentee) an.
              </div>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:18 }}>
              <div style={{ background:'#0f172a', border:'1px solid #1f2a44', borderRadius:14, padding:16 }}>
                <div style={{ fontWeight:700, fontSize:16, marginBottom:6, color:'#93c5fd' }}>Woche 1 — Ankommen & Orientierung</div>
                <div style={{ fontSize:14, marginBottom:8 }}><strong>Hauptziel:</strong> Mentee fühlt sich willkommen, versteht die Basics und weiß, wo er/sie starten soll.</div>
                <div style={{ fontSize:14, marginBottom:6, color:'#cbd5e1' }}>Empfohlene Schritte:</div>
                <ul style={{ margin:'0 0 8px 18px' }}>
                  <li>Begrüßung + persönliche Vorstellung.</li>
                  <li>Rahmen erklären: Dauer, Grenzen, Kommunikationswege, Verfügbarkeit.</li>
                  <li>Erwartungen/Bedarfe des Mentees abholen.</li>
                  <li>1–3 einfache Einstiegsziele definieren (z. B. Plattform verstehen, Supportplan nutzen, erstes Event).</li>
                  <li>Plattform-Tour: Dashboard, Supportplan, Punkte, Achievements.</li>
                  <li>Discord-Tour: wichtige Channels, wo man Fragen stellt, wo Events laufen.</li>
                  <li>Regeln/Spirit kurz erklären, Missverständnisse direkt klären.</li>
                  <li>Erste typische Fragen beantworten.</li>
                  <li>Mini-Check-In am Ende der Woche: „Kommst du klar? Was ist noch offen?“</li>
                </ul>
                <div style={{ fontSize:14 }}><strong>Ergebnis Ende Woche 1:</strong> Mentee kennt die Wege, versteht die wichtigsten Systeme und hat einen Plan.</div>
              </div>

              <div style={{ background:'#0f172a', border:'1px solid #1f2a44', borderRadius:14, padding:16 }}>
                <div style={{ fontWeight:700, fontSize:16, marginBottom:6, color:'#93c5fd' }}>Woche 2 — Technik, Tools & erste echte Aktivität</div>
                <div style={{ fontSize:14, marginBottom:8 }}><strong>Hauptziel:</strong> Mentee wird praktisch aktiv – Support, Tools, Teilnahme.</div>
                <div style={{ fontSize:14, marginBottom:6, color:'#cbd5e1' }}>Empfohlene Schritte:</div>
                <ul style={{ margin:'0 0 8px 18px' }}>
                  <li>Setup-Stand erfragen + Hauptbaustellen finden.</li>
                  <li>Wenn nötig: Stream-Basics oder Troubleshooting begleiten.</li>
                  <li>Lurker-Client / Support-Tools erklären und gemeinsam einrichten.</li>
                  <li>Mentee aktiv im Streamplan begleiten (erste Eintragungen/Supportabläufe).</li>
                  <li>Events/Challenges vorstellen und Teilnahme gemeinsam anstoßen.</li>
                  <li>Zur Wochenmitte ein kurzer Touchpoint: „Was hat geklappt? Wo hakt’s?“</li>
                </ul>
                <div style={{ fontSize:14 }}><strong>Ergebnis Ende Woche 2:</strong> Mentee nutzt Plattform/Support aktiv, Tools laufen, erste Community-Teilnahme startet.</div>
              </div>

              <div style={{ background:'#0f172a', border:'1px solid #1f2a44', borderRadius:14, padding:16 }}>
                <div style={{ fontWeight:700, fontSize:16, marginBottom:6, color:'#93c5fd' }}>Woche 3 — Wachstum, Routine & Feedback</div>
                <div style={{ fontSize:14, marginBottom:8 }}><strong>Hauptziel:</strong> Mentee stabilisiert seine/ihre Routine und bekommt gezieltes Feedback.</div>
                <div style={{ fontSize:14, marginBottom:6, color:'#cbd5e1' }}>Empfohlene Schritte:</div>
                <ul style={{ margin:'0 0 8px 18px' }}>
                  <li>Erste Erfahrungen reflektieren (Support, Event/Challenge, Plattform).</li>
                  <li>Stream/Content anschauen (wenn möglich).</li>
                  <li>Feedback geben: konkret, freundlich, machbar.</li>
                  <li>Positive Stärken betonen + 1–2 Entwicklungsfelder definieren.</li>
                  <li>Mentee motivieren, Fortschritte sichtbar zu machen (z. B. regelmäßige Aktivität / Challenge-Teilnahme).</li>
                  <li>Kurzer Check-In am Ende der Woche: „Wie fühlt sich das Network mittlerweile an?“</li>
                </ul>
                <div style={{ fontSize:14 }}><strong>Ergebnis Ende Woche 3:</strong> Mentee hat Routine, weiß, woran er/sie arbeiten kann, fühlt sich eingebunden.</div>
              </div>

              <div style={{ background:'#0f172a', border:'1px solid #1f2a44', borderRadius:14, padding:16 }}>
                <div style={{ fontWeight:700, fontSize:16, marginBottom:6, color:'#93c5fd' }}>Woche 4 — Selbstständigkeit & Abschluss</div>
                <div style={{ fontSize:14, marginBottom:8 }}><strong>Hauptziel:</strong> Mentee braucht dich nicht mehr „als Krücke“, sondern ist stabil im Network.</div>
                <div style={{ fontSize:14, marginBottom:6, color:'#cbd5e1' }}>Empfohlene Schritte:</div>
                <ul style={{ margin:'0 0 8px 18px' }}>
                  <li>Fortschritt zu den Start-Zielen prüfen.</li>
                  <li>Letzte offene Fragen klären.</li>
                  <li>„Was machst du ab jetzt selbst?“ festziehen (Support, Tools, Event-Routine).</li>
                  <li>Abschluss-Feedback geben (inkl. Lob + klare nächste Schritte).</li>
                  <li>Mentoring offiziell beenden.</li>
                  <li>Optional: Perspektive öffnen („Wenn du später willst, kannst du selbst Mentor werden“).</li>
                  <li>Falls nötig: Verlängerung nur bei echtem Bedarf und klar definiertem Ziel.</li>
                </ul>
                <div style={{ fontSize:14 }}><strong>Ergebnis Ende Woche 4:</strong> Mentee ist eigenständig, integriert und weiß, wie er/sie im AP-Network wächst.</div>
              </div>
            </div>

            <div style={{ marginTop:18, background:'#0f172a', border:'1px solid #1f2a44', borderRadius:14, padding:16 }}>
              <div style={{ fontWeight:700, fontSize:16, marginBottom:8, color:'#f0abfc' }}>Mini-Faustregeln für Mentoren (intern)</div>
              <ul style={{ margin:'0 0 0 18px' }}>
                <li>Mentor sein heißt begleiten, nicht bewerten.</li>
                <li>Qualität vor Quantität: Lieber 1 Mentee gut als 2 halb.</li>
                <li>Erst zuhören, dann helfen.</li>
                <li>Keine Lösung ohne Kontext: immer erst verstehen, dann empfehlen.</li>
                <li>Immer positiv rahmen: Kritik = Chance, nicht Urteil.</li>
                <li>Aktivität nachweisbar halten: mind. 1 Interaktion pro Woche.</li>
              </ul>
            </div>

            <div style={{ display:'flex', justifyContent:'flex-end', marginTop:16 }}>
              <button type="button" onClick={()=> setGuideOpen(false)} style={{ background:'#1e293b', color:'#e2e8f0', border:'1px solid #334155', borderRadius:10, padding:'10px 16px', cursor:'pointer' }}>Verstanden</button>
            </div>
          </div>
        </div>
      </div>
    )}
    {checklistOpen && (
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:2050, display:'flex', alignItems:'center', justifyContent:'center', padding:'24px' }} onClick={()=> !checklistSaving && setChecklistOpen(false)}>
        <div role="dialog" aria-modal="true" aria-label="Mentor-Checkliste" onClick={e=> e.stopPropagation()} style={{ width:'100%', maxWidth:1000, maxHeight:'86vh', background:'#0d1b29', border:'1px solid #1e3347', borderRadius:18, boxShadow:'0 18px 50px -6px rgba(0,0,0,0.55)', display:'flex', flexDirection:'column' }}>
          <div style={{ padding:'18px 24px', borderBottom:'1px solid #1e3347', display:'flex', alignItems:'center', justifyContent:'space-between', background:'linear-gradient(180deg, rgba(14,120,175,0.35), rgba(2,6,23,0))', color:'#e2e8f0' }}>
            <h2 style={{ margin:0, fontSize:20 }}>Mentor-Checkliste</h2>
            <div style={{ display:'flex', gap:10 }}>
              <button type="button" disabled={checklistSaving} onClick={saveChecklist} style={{ background:'#065f46', color:'#d1fae5', border:'1px solid #10b981', padding:'8px 16px', fontSize:13, borderRadius:10, cursor: checklistSaving ? 'default':'pointer', opacity: checklistSaving?0.7:1 }}>Speichern{checklistSaving?'…':''}</button>
              <button type="button" onClick={()=> !checklistSaving && setChecklistOpen(false)} style={{ background:'transparent', color:'#93c5fd', border:'1px solid #334155', padding:'8px 14px', fontSize:13, borderRadius:10, cursor:'pointer' }}>Schließen</button>
            </div>
          </div>
          <div style={{ padding:'16px 20px', fontSize:13, color:'#cbd5e1' }}>
            <p style={{ margin:'0 0 12px', lineHeight:1.5 }}>Hinweis: Diese Checkliste ist ein Arbeitswerkzeug. Nicht jeder Punkt muss in jeder Betreuung exakt gleich ablaufen — aber sie hilft, nichts zu vergessen und Mentoring sauber & fair zu halten.</p>
            {checklistError && <div style={{ background:'rgba(220,38,38,0.18)', border:'1px solid rgba(248,113,113,0.5)', color:'#fecaca', padding:'8px 10px', borderRadius:8, marginBottom:12 }}>{checklistError}</div>}
          </div>
          <div style={{ flex:1, overflowY:'auto', padding:'0 20px 20px', display:'flex', flexDirection:'column', gap:18 }}>
            {checklistLoading ? (
              <div style={{ color:'#94a3b8', fontSize:14 }}>Lade Checkliste…</div>
            ) : (
              Object.keys(checklistGroups).map(group => (
                <div key={group} style={{ background:'#112638', border:'1px solid #1e3347', borderRadius:14, padding:16 }}>
                  <div style={{ fontWeight:600, fontSize:15, marginBottom:10, color:'#93c5fd' }}>{group}</div>
                  <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                    {checklistGroups[group].map(it => (
                      <div key={it.key} style={{ display:'flex', flexDirection:'column', gap:6, background: it.checked ? 'linear-gradient(90deg, rgba(6,95,70,0.55), rgba(6,95,70,0.15))':'#162b3b', border:'1px solid '+(it.checked ? '#10b981':'#1e3347'), padding:'10px 12px', borderRadius:10 }}>
                        <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
                          <input type="checkbox" checked={!!it.checked} onChange={() => toggleChecklistItem(it.key)} style={{ width:16, height:16 }} />
                          <span style={{ fontSize:14, color:'#e2e8f0' }}>{renderChecklistLabel(it.key)}</span>
                          {it.checkedAt && (
                            <span style={{ marginLeft:'auto', fontSize:11, color:'#d1fae5', background:'rgba(16,185,129,0.18)', padding:'2px 6px', borderRadius:6 }}>
                              {formatChecklistDate(it.checkedAt)}
                            </span>
                          )}
                        </label>
                        <textarea
                          value={it.note || ''}
                          onChange={e => updateChecklistNote(it.key, e.target.value.slice(0,300))}
                          placeholder="Notiz (optional)"
                          style={{ width:'100%', resize:'vertical', minHeight:50, background:'#0d1b29', border:'1px solid #1e3347', color:'#e2e8f0', fontSize:12, padding:'6px 8px', borderRadius:8 }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
}

// Helper: human readable label mapping
function renderChecklistLabel(key) {
  const map = {
    mentee_begruesst: 'Mentee aktiv begrüßt',
    mentor_vorgestellt: 'Mentor kurz vorgestellt (Name, Schwerpunkt, wie erreichbar)',
    ziel_erklaert: 'Mentoring-Ziel erklärt („Begleitung auf Augenhöhe, kein Ranking“)',
    dauer_erklaert: 'Dauer des Mentorings erklärt (Standard: ca. 4 Wochen)',
    grenzen_erklaert: 'Grenzen erklärt (keine 24/7-Pflicht)',
    kommunikationsweg_abgesprochen: 'Kommunikationsweg(e) abgesprochen (Discord DM/Call/Plattformchat)',
    erreichbarkeit_geteilt: 'Erreichbarkeitszeiten geteilt',
    mentee_erwartungen_gefragt: 'Erwartungen/Ziele abgefragt',
    start_ziele_definiert: 'Gemeinsame Start-Ziele definiert (1–3 konkrete Punkte)',
    erster_check_in_termin: 'Erster Check-In Termin vereinbart (z. B. nach 2–3 Tagen)',
    grundprinzip_erklaert: 'Grundprinzip AP-Network erklärt (Support-Kultur, Fairness, Qualität)',
    rollen_erklaert: 'Rollen erklärt (Anwärter, Member, Mentor, Mods und Admins)',
    discord_areas_erklaert: 'Wichtige Discord-Areas gezeigt/erklärt',
    plattform_bereiche_erklaert: 'Wichtige Plattform-Bereiche gezeigt/erklärt',
    regeln_erklaert: 'Regeln/Grundwerte erklärt',
    typische_fragen_beantwortet: 'Typische Anfängerfragen beantwortet',
    erste_interaktionen_motiviert: 'Zu ersten Interaktionen motiviert',
    dashboard_geoeffnet: 'Plattform-Dashboard gemeinsam geöffnet',
    streamplan_gezeigt: 'Streamplan gezeigt',
    streamplan_logik_erklaert: 'Streamplan-Logik erklärt (wie/wo/warum man sich einträgt)',
    punkte_logik_erklaert: 'Punkte-Grundlogik erklärt (woher Punkte kommen)',
    punkte_aktionen_erklaert: 'Konkrete Punkte-Aktionen erklärt (was zählt/was nicht zählt)',
    achievements_erklaert: 'Achievements erklärt & gezeigt',
    punkte_rueckfragen_beantwortet: 'Rückfragen zu Punkten beantwortet',
    basics_selbststaendig_test: 'Mentee kann die Basics selbstständig bedienen',
    setup_stand_erfragt: 'Setup-Stand erfragt (Software/Hardware/Streaming-Routine)',
    hauptbaustellen_identifiziert: 'Hauptbaustelle(n) identifiziert (Ton/Bild/Scenes/Chat/Internet)',
    quick_wins_vorgeschlagen: 'Technische Quick-Wins vorgeschlagen',
    setup_test_durchgesprochen: 'Setup-Test durchgesprochen (Ton/Bild/Run-through)',
    fehlerquellen_genannt: 'Typische Fehlerquellen genannt + wie man sie selbst checkt',
    lurker_zweck_erklaert: 'Zweck des Lurker-Clients erklärt',
    lurker_installation_begleitet: 'Installation/Start erklärt oder begleitet',
    lurker_login_flow: 'Login-/Profil-Flow erklärt oder begleitet',
    lurker_einstellungen_erklaert: 'Relevante Einstellungen erklärt',
    lurker_fair_use: 'Fair-Use Hinweise erklärt (korrekte Nutzung im Network)',
    lurker_troubleshooting: 'Troubleshooting begleitet',
    events_gezeigt: 'Events gezeigt/erklärt',
    challenges_gezeigt: 'Challenges gezeigt/erklärt',
    teilnahme_regeln_erklaert: 'Teilnahme-Regeln erklärt',
    erste_anmeldung_begleitet: 'Erste Anmeldung begleitet',
    teilnahme_motiviert: 'Mentee motiviert, an mindestens einem Format teilzunehmen',
    feedback_bedarf_gefragt: 'Feedback-Bedarf erfragt',
    stream_angeschaut: 'Stream/Content angeschaut',
    feedback_stream_gegeben: 'Feedback zum Stream gegeben',
    feedback_auftreten_gegeben: 'Feedback Auftreten/Moderation',
    feedback_community_gegeben: 'Feedback Community-Verhalten',
    positive_stärken: 'Positive Stärken betont',
    naechste_schritte_definiert: 'umsetzbare nächste Schritte/Verbesserungen vorgeschlagen',
    feedback_anwenden_motiviert: 'Mentee um sein Feedback gebeten',
    interaktion_pro_woche: 'Mind. 1 Interaktion pro Woche',
    regelmaessige_check_ins: 'Regelmäßige Check-Ins durchgeführt',
    fortschritt_geprueft: 'Fortschritt zu Start-Zielen geprüft',
    selbststaendigkeit_vorbereitet: 'Auf Selbstständigkeit vorbereitet',
    abschluss_check_in: 'Abschluss-Check-In gemacht („Was läuft gut? Was fehlt noch?“)',
    verlaengerung_besprochen: 'Bei Bedarf Verlängerung besprochen (nur wenn sinnvoll)',
    mentoring_beendet_motiviert: 'Mentoring offiziell beendet + Mentee ermutigt, später selbst Mentor zu werden'
  };
  return map[key] || key;
}

function formatChecklistDate(ts) {
  try { return new Date(ts).toLocaleDateString('de-DE', { year:'numeric', month:'2-digit', day:'2-digit' }); } catch { return ts; }
}

